import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import PermissionWrapper from '../components/PermissionWrapper';
import {
  fetchBrandSupplierMappings,
  fetchBrandSupplierOptions,
  removeBrandSupplierMapping,
  saveBrandSupplierMapping,
  type BrandSupplierMapping,
  type BrandSupplierOption,
} from '../services/brandSupplierService';

function getErrorMessage(error: unknown, fallback: string): string {
  if (axios.isAxiosError<{ message?: string }>(error)) {
    return error.response?.data?.message || fallback;
  }
  return fallback;
}

const BrandSupplierManagement: React.FC = () => {
  const [mappings, setMappings] = useState<BrandSupplierMapping[]>([]);
  const [suppliers, setSuppliers] = useState<BrandSupplierOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<BrandSupplierMapping | null>(null);
  const [brandName, setBrandName] = useState('');
  const [supplierId, setSupplierId] = useState<number | ''>('');
  const [deleteTarget, setDeleteTarget] = useState<BrandSupplierMapping | null>(
    null,
  );
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [mappingRows, supplierRows] = await Promise.all([
        fetchBrandSupplierMappings(),
        fetchBrandSupplierOptions(),
      ]);
      setMappings(mappingRows);
      setSuppliers(supplierRows);
    } catch (e: unknown) {
      setError(getErrorMessage(e, 'Kunde inte hämta brands och suppliers.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const filteredMappings = useMemo(() => {
    const query = search.trim().toLocaleLowerCase('sv-SE');
    if (!query) return mappings;
    return mappings.filter((item) =>
      `${item.brandName} ${item.supplierName}`
        .toLocaleLowerCase('sv-SE')
        .includes(query),
    );
  }, [mappings, search]);

  const openCreate = () => {
    setEditing(null);
    setBrandName('');
    setSupplierId('');
    setDialogOpen(true);
  };

  const openEdit = (mapping: BrandSupplierMapping) => {
    setEditing(mapping);
    setBrandName(mapping.brandName);
    setSupplierId(mapping.supplierId);
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!brandName.trim() || supplierId === '') return;
    setSaving(true);
    setError('');
    try {
      await saveBrandSupplierMapping({
        brandId: editing?.brandId,
        brandName: brandName.trim(),
        supplierId: Number(supplierId),
      });
      setDialogOpen(false);
      setNotice(
        editing ? 'Brand-mappningen uppdaterades.' : 'Brand lades till.',
      );
      await load();
    } catch (e: unknown) {
      setError(getErrorMessage(e, 'Kunde inte spara brand-mappningen.'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setSaving(true);
    try {
      await removeBrandSupplierMapping(deleteTarget.brandId);
      setDeleteTarget(null);
      setNotice('Brand-mappningen togs bort.');
      await load();
    } catch (e: unknown) {
      setError(getErrorMessage(e, 'Kunde inte ta bort brand-mappningen.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <PermissionWrapper requiredPermissions={['manage_companies']}>
      <Box sx={{ maxWidth: 1200, mx: 'auto', p: { xs: 2, md: 3 } }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          justifyContent="space-between"
          alignItems={{ xs: 'stretch', sm: 'center' }}
          sx={{ mb: 2.5 }}
        >
          <Box>
            <Typography variant="h4" fontWeight={700}>
              Brands och suppliers
            </Typography>
            <Typography color="text.secondary" sx={{ mt: 0.5 }}>
              Lägg till brands och säkerställ att varje brand är kopplat till
              rätt supplier.
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={() => void load()}
              disabled={loading}
            >
              Uppdatera
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={openCreate}
              disabled={suppliers.length === 0}
            >
              Lägg till brand
            </Button>
          </Stack>
        </Stack>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}
        {suppliers.length === 0 && !loading && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Viskan returnerade inga suppliers med ett numeriskt supplier number.
          </Alert>
        )}

        <Card>
          <CardContent>
            <TextField
              fullWidth
              size="small"
              label="Sök brand eller supplier"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              sx={{ mb: 2 }}
            />
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <CircularProgress />
              </Box>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Brand</TableCell>
                      <TableCell>Supplier</TableCell>
                      <TableCell align="right">Åtgärder</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredMappings.map((mapping) => (
                      <TableRow key={mapping.brandId} hover>
                        <TableCell sx={{ fontWeight: 600 }}>
                          {mapping.brandName}
                        </TableCell>
                        <TableCell>
                          {mapping.supplierName}{' '}
                          <Typography
                            component="span"
                            variant="caption"
                            color="text.secondary"
                          >
                            (Suppliernr {mapping.supplierId})
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Button
                            size="small"
                            startIcon={<EditOutlinedIcon />}
                            onClick={() => openEdit(mapping)}
                          >
                            Ändra
                          </Button>
                          <Button
                            size="small"
                            color="error"
                            startIcon={<DeleteOutlineIcon />}
                            onClick={() => setDeleteTarget(mapping)}
                          >
                            Ta bort
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                    {filteredMappings.length === 0 && (
                      <TableRow>
                        <TableCell
                          colSpan={3}
                          align="center"
                          sx={{ py: 5, color: 'text.secondary' }}
                        >
                          {mappings.length === 0
                            ? 'Inga brands är registrerade ännu.'
                            : 'Inga resultat matchar sökningen.'}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>

        <Dialog
          open={dialogOpen}
          onClose={() => !saving && setDialogOpen(false)}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle>
            {editing ? 'Ändra brand-mappning' : 'Lägg till brand'}
          </DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ pt: 1 }}>
              <TextField
                autoFocus
                fullWidth
                label="Brandnamn"
                value={brandName}
                onChange={(event) => setBrandName(event.target.value)}
                disabled={Boolean(editing)}
                helperText={
                  editing
                    ? 'Brandnamnet används av befintliga produkter och kan inte ändras här.'
                    : undefined
                }
              />
              <FormControl fullWidth>
                <InputLabel id="brand-supplier-label">Supplier</InputLabel>
                <Select
                  labelId="brand-supplier-label"
                  label="Supplier"
                  value={supplierId}
                  onChange={(event) =>
                    setSupplierId(Number(event.target.value))
                  }
                >
                  {suppliers.map((supplier) => (
                    <MenuItem
                      key={supplier.supplierId}
                      value={supplier.supplierId}
                    >
                      {supplier.supplierName} (Suppliernr {supplier.supplierId})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialogOpen(false)} disabled={saving}>
              Avbryt
            </Button>
            <Button
              variant="contained"
              onClick={() => void handleSave()}
              disabled={saving || !brandName.trim() || supplierId === ''}
            >
              {saving ? 'Sparar…' : 'Spara'}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={Boolean(deleteTarget)}
          onClose={() => !saving && setDeleteTarget(null)}
        >
          <DialogTitle>Ta bort brand-mappning?</DialogTitle>
          <DialogContent>
            <DialogContentText>
              {deleteTarget
                ? `${deleteTarget.brandName} tas bort från registret och kan inte längre kopplas till ${deleteTarget.supplierName}.`
                : ''}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteTarget(null)} disabled={saving}>
              Avbryt
            </Button>
            <Button
              color="error"
              variant="contained"
              onClick={() => void handleDelete()}
              disabled={saving}
            >
              {saving ? 'Tar bort…' : 'Ta bort'}
            </Button>
          </DialogActions>
        </Dialog>
        <Snackbar
          open={Boolean(notice)}
          autoHideDuration={3500}
          onClose={() => setNotice('')}
          message={notice}
        />
      </Box>
    </PermissionWrapper>
  );
};

export default BrandSupplierManagement;
